// SettingsPage — v2 Settings shell composer (CHG-1239).
//
// Layout: <PageHeader> + flex row of <SettingsSectionRail> + <SettingsForm>.
// URL state via useSearchParams("section") so /settings?section=brand_identity
// deep-links to the brand-identity section and refresh preserves the choice.
//
// Pure slot composition — the page (apps/dashboard-v2/src/pages/Settings.tsx)
// owns the query/mutation; this primitive renders chrome + dispatches the
// section's values + handles URL state.
//
// CHG-1295 (minor-version bump, additive — mirrors CHG-1291 PageHeader and
// CHG-1292 DetailPage / BulkActionBar / SettingsForm extension precedent):
// SettingsPage now forwards the active section's optional `save_permission`
// code into SettingsForm's CHG-1292 `savePermission` prop. Sections that
// don't declare a `save_permission` continue to render an ungated Save
// button (existing callers unchanged). The forwarded prop shape is
// { require, fallback: "disable" }; "disable" is the chosen fallback so
// users with read-only Settings access see the Save button greyed out
// with the canonical denied tooltip, rather than the affordance vanishing.
// Closes CHG-1294 deferred follow-up item #1.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader, type PageHeaderAction } from "./PageHeader";
import { SettingsSectionRail } from "./SettingsSectionRail";
import { SettingsForm } from "./SettingsForm";
import type { SettingsSection } from "../../lib/settings-sections";

export interface SettingsPageProps {
  sections: SettingsSection[];
  /** Per-section value map: {sectionId: {fieldId: value}}. */
  values: Record<string, Record<string, unknown>>;
  onChange: (sectionId: string, fieldId: string, value: unknown) => void;
  /** Save the current active section's values. Called from the form's Save button. */
  onSaveSection: (sectionId: string) => Promise<void> | void;
  /** Per-section dirty flag. Caller computes from a baseline values snapshot. */
  dirty?: Record<string, boolean>;
  busy?: boolean;
  error?: string;
  /** Title rendered by PageHeader; defaults to "Settings". */
  title?: string;
  /** Extra header actions (right side of PageHeader). Save is rendered by SettingsForm, not here. */
  headerActions?: PageHeaderAction[];
  /**
   * CHG-2942 — forwarded to SettingsForm for fields declaring an inline
   * `action` (SettingsFieldAction); additive, existing callers unchanged.
   */
  onFieldAction?: (sectionId: string, fieldId: string, actionId: string) => void;
}

export function SettingsPage({
  sections,
  values,
  onChange,
  onSaveSection,
  dirty,
  busy,
  error,
  title = "Settings",
  headerActions,
  onFieldAction,
}: SettingsPageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSectionId = searchParams.get("section");
  const fallback = sections[0]?.section_id ?? "";
  const activeSectionId = useMemo(() => {
    if (urlSectionId && sections.some((s) => s.section_id === urlSectionId)) return urlSectionId;
    return fallback;
  }, [urlSectionId, fallback, sections]);

  const activeSection = sections.find((s) => s.section_id === activeSectionId) ?? sections[0];

  const selectSection = (sectionId: string) => {
    if (sectionId === activeSectionId) return;
    const next = new URLSearchParams(searchParams);
    next.set("section", sectionId);
    setSearchParams(next, { replace: false });
  };

  if (!activeSection) {
    return (
      <div style={{ padding: 32 }}>
        <PageHeader title={title} subtitle="No sections configured." />
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "0 24px 24px", height: "100%", minHeight: 0 }}>
      <PageHeader title={title} subtitle="Manage organization settings." actions={headerActions} />

      <div
        data-settings-shell
        style={{
          display: "flex",
          gap: 20,
          flex: 1,
          minHeight: 0,
          paddingTop: 8,
        }}
      >
        <SettingsSectionRail
          sections={sections}
          activeSectionId={activeSectionId}
          onSelect={selectSection}
        />
        <SettingsForm
          section={activeSection}
          values={values[activeSectionId] ?? {}}
          onChange={(fieldId, value) => onChange(activeSectionId, fieldId, value)}
          onSave={() => onSaveSection(activeSectionId)}
          busy={busy}
          error={error}
          dirty={dirty?.[activeSectionId] ?? false}
          savePermission={
            activeSection.save_permission
              ? { require: activeSection.save_permission, fallback: "disable" }
              : undefined
          }
          onFieldAction={
            onFieldAction
              ? (fieldId, actionId) => onFieldAction(activeSectionId, fieldId, actionId)
              : undefined
          }
        />
      </div>
    </div>
  );
}
